// src/components/SystemicAlertsPanel.jsx
// AI-detected recurring patterns. Trend badges + actionable summary.

import React, { useState as useState_SA } from "react";
import * as PPIcons from "./_icons";
import { Button, CategoryChip, Empty, SectionHead, SeverityChip } from "./_primitives";

const TREND_META = {
  new:        { label: "New",        color: "var(--pp-new)",          bg: "var(--pp-new-bg)" },
  growing:    { label: "Growing",    color: "var(--pp-crit)",         bg: "var(--pp-crit-bg)" },
  ongoing:    { label: "Ongoing",    color: "var(--pp-investigating)", bg: "var(--pp-investigating-bg)" },
  persistent: { label: "Persistent", color: "var(--pp-mod)",          bg: "var(--pp-mod-bg)" },
  declining:  { label: "Declining",  color: "var(--pp-low)",          bg: "var(--pp-low-bg)" },
};

function SystemicAlertsPanel({ alerts = [], onDismiss, onRemovePost, onRunAnalysis }) {
  return (
    <div className="pp-surface">
      <SectionHead
        title="Systemic alerts"
        sub="AI-detected recurring patterns across posts"
        right={
          <Button variant="secondary" size="sm"
            icon={<PPIcons.IconRefresh />}
            onClick={onRunAnalysis}>
            Run analysis
          </Button>
        }
      />
      <div style={{ padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
        {alerts.length === 0 ? (
          <Empty
            icon={<PPIcons.IconShield />}
            title="No systemic patterns yet"
            sub="The system scans every 12 hours, or run an analysis on demand."
          />
        ) : alerts.map(a => <Alert key={a.id} alert={a} onDismiss={onDismiss} />)}
      </div>
    </div>
  );
}

function Alert({ alert, onDismiss }) {
  const [open, setOpen] = useState_SA(false);
  const trend = TREND_META[alert.trend] || TREND_META.ongoing;
  return (
    <article style={{
      border: "1px solid var(--border)", borderRadius: "var(--radius)",
      background: "var(--surface)",
    }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          padding: 14, display: "flex", alignItems: "flex-start", gap: 12,
          cursor: "pointer",
        }}>
        <div style={{
          flex: "0 0 6px", alignSelf: "stretch", background: trend.color,
          borderRadius: 999, margin: "2px 0",
        }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 4 }}>
            <span className="pp-chip" style={{
              background: trend.bg, color: trend.color,
              border: `1px solid ${trend.color}40`,
            }}>
              <PPIcons.IconTrending style={{ width: 11, height: 11 }} />
              {trend.label}
            </span>
            <SeverityChip severity={alert.severity} />
            <CategoryChip category={alert.category} />
            <span style={{
              fontSize: 11, color: "var(--text-faint)",
              fontFamily: "var(--font-mono)", marginLeft: 4,
            }}>
              {(alert.postIds || []).length} posts · {alert.windowDays}d window
            </span>
          </div>
          <h3 style={{
            fontSize: 15, fontWeight: 700, color: "var(--text-strong)",
            letterSpacing: "-0.005em",
          }}>{alert.title}</h3>
          <p style={{
            fontSize: 13, color: "var(--text-dim)", lineHeight: 1.5, marginTop: 4,
          }}>{alert.summary}</p>
        </div>
        <PPIcons.IconChevD style={{
          width: 16, height: 16, color: "var(--text-faint)", marginTop: 2,
          transform: open ? "rotate(180deg)" : "none",
          transition: "transform .15s",
        }} />
      </div>
      {open && (
        <div style={{
          padding: "0 14px 14px 32px", display: "flex", flexDirection: "column", gap: 10,
        }}>
          <div style={{
            padding: 12, background: "var(--accent-soft)",
            border: "1px solid var(--accent-soft-bd)",
            borderRadius: "var(--radius-sm)",
          }}>
            <div style={{
              fontSize: 11, color: "var(--accent)", textTransform: "uppercase",
              letterSpacing: 0.06, fontWeight: 700, marginBottom: 4,
              display: "inline-flex", alignItems: "center", gap: 4,
            }}>
              <PPIcons.IconSparkle style={{ width: 11, height: 11 }} /> Root cause hypothesis
            </div>
            <div style={{ fontSize: 13, color: "var(--text)", lineHeight: 1.5 }}>
              {alert.rootCause}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Button variant="primary" size="sm" icon={<PPIcons.IconSparkle />}>
              Generate task
            </Button>
            <Button variant="secondary" size="sm">View linked posts</Button>
            <Button variant="ghost" size="sm" onClick={() => onDismiss && onDismiss(alert)}>
              Dismiss
            </Button>
          </div>
        </div>
      )}
    </article>
  );
}

export default SystemicAlertsPanel;
